"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ScrollToTop } from "@/components/ui/scroll-to-top";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import {
  CheckCircle2,
  Clock,
  CreditCard,
  HelpCircle,
  Mail,
  RefreshCcw,
  Shield,
  XCircle,
} from "lucide-react";
import { PublicLink as Link } from "@/components/public/PublicLink";

export default function RefundPolicyPage() {
  const { t } = useJsonTranslation("RefundPolicy");

  return (
    <div className="container mx-auto px-4 py-8 pt-24 sm:py-16">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="bg-leaf-500/20 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl">
            <RefreshCcw className="text-leaf-400 h-8 w-8" />
          </div>
          <h1 className="text-foreground mb-4 text-4xl font-bold">
            {t("title")}
          </h1>
          <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{t("lastUpdated")}</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Overview */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="text-leaf-400 h-5 w-5" />
                {t("sections.commitment")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.commitment.p")}</p>
              <div className="bg-leaf-500/10 border-leaf-500/30 rounded-lg border p-4">
                <p className="text-leaf-400 font-medium">
                  {t("content.commitment.guarantee")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Refund Eligibility */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="text-leaf-400 h-5 w-5" />
                {t("sections.eligibility")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-6">
              <div>
                <h4 className="text-foreground mb-3 flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="text-leaf-400 h-4 w-4" />
                  {t("content.eligibility.eligible")}
                </h4>
                <ul className="space-y-2">
                  {[0, 1, 2, 3].map((i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                      <span>{t(`content.eligibility.eligibleItems.${i}`)}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Separator />
              <div>
                <h4 className="text-foreground mb-3 flex items-center gap-2 font-semibold">
                  <XCircle className="text-leaf-400 h-4 w-4" />
                  {t("content.eligibility.notEligible")}
                </h4>
                <ul className="space-y-2">
                  {[0, 1, 2, 3].map((i) => (
                    <li key={i} className="flex items-start gap-2">
                      <XCircle className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                      <span>
                        {t(`content.eligibility.notEligibleItems.${i}`)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How to Request */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="text-leaf-400 h-5 w-5" />
                {t("sections.howToRequest")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <ol className="space-y-4">
                <li className="flex gap-3">
                  <span className="bg-leaf-500/20 text-leaf-600 dark:text-leaf-400 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold">
                    1
                  </span>
                  <span>
                    <strong className="text-foreground">
                      {t("content.howToRequest.step1")}{" "}
                    </strong>
                    {t("content.howToRequest.step1Desc")}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="bg-leaf-500/20 text-leaf-600 dark:text-leaf-400 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold">
                    2
                  </span>
                  <span>
                    <strong className="text-foreground">
                      {t("content.howToRequest.step2")}{" "}
                    </strong>
                    {t("content.howToRequest.step2Desc")}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="bg-leaf-500/20 text-leaf-600 dark:text-leaf-400 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold">
                    3
                  </span>
                  <span>
                    <strong className="text-foreground">
                      {t("content.howToRequest.step3")}{" "}
                    </strong>
                    {t("content.howToRequest.step3Desc")}
                  </span>
                </li>
              </ol>
            </CardContent>
          </Card>

          {/* Cancellation vs Refund */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="text-leaf-400 h-5 w-5" />
                {t("sections.cancellationVsRefund")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="bg-foreground/5 rounded-lg p-4">
                  <h5 className="text-foreground mb-2 font-medium">
                    {t("content.cancellationVsRefund.cancellation")}
                  </h5>
                  <p className="text-sm">
                    {t("content.cancellationVsRefund.cancellationDesc")}
                  </p>
                </div>
                <div className="bg-foreground/5 rounded-lg p-4">
                  <h5 className="text-foreground mb-2 font-medium">
                    {t("content.cancellationVsRefund.refund")}
                  </h5>
                  <p className="text-sm">
                    {t("content.cancellationVsRefund.refundDesc")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trial Period */}
          <Card variant="glass">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="text-leaf-400 h-5 w-5" />
                {t("sections.freeTrial")}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground space-y-4">
              <p>{t("content.freeTrial.p")}</p>
              <ul className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="text-leaf-400 mt-1 h-4 w-4 flex-shrink-0" />
                    <span>{t(`content.freeTrial.items.${i}`)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card
            variant="glass"
            className="from-leaf-500/10 to-leaf-500/10 bg-gradient-to-r"
          >
            <CardContent className="pt-6 text-center">
              <Mail className="text-leaf-600 dark:text-leaf-400 mx-auto mb-4 h-8 w-8" />
              <h3 className="text-foreground mb-2 text-xl font-semibold">
                {t("sections.questions")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t("content.questions.p")}
              </p>
              <Link href="/contact">
                <Button className="bg-leaf-500 hover:bg-leaf-600">
                  {t("content.questions.button")}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      <ScrollToTop />
    </div>
  );
}
